// ============================================================
// SERVICES DATA
// ============================================================

export const servicesData = {
  // ==========================================================
  // SOFTWARE DEVELOPMENT
  // ==========================================================

  softwareDevelopment: {
    id: "softwareDevelopment",

    name: "Software Development",

    shortDescription:
      "Scalable, secure, and customized software solutions",

    description:
      "We build scalable, secure, and customized software solutions designed around your business requirements, workflows, and long-term growth.",

    subServices: [
      {
        id: "customSoftware",

        name: "Custom Software Development",

        description:
          "We develop customized software solutions tailored to your business processes, workflows, and specific operational requirements.",
      },

      {
        id: "mobileApp",

        name: "Mobile App Development",

        description:
          "We create modern and scalable mobile applications for businesses, focusing on performance, usability, security, and a smooth user experience.",
      },

      {
        id: "webDevelopment",

        name: "Web Development",

        description:
          "We develop responsive, scalable, and high-performance websites and web applications that help businesses establish and grow their digital presence.",
      },

      {
        id: "uiux",

        name: "UI/UX Development",

        description:
          "We design intuitive and engaging user interfaces with a strong focus on usability, accessibility, visual consistency, and user experience.",
      },

      {
        id: "crm",

        name: "CRM Development",

        description:
          "We build customized CRM solutions that help businesses manage customers, leads, sales processes, communication, and business relationships efficiently.",
      },

      {
        id: "mlm",

        name: "MLM Development",

        description:
          "We develop scalable MLM software solutions with features for network management, member management, commissions, genealogy, and business operations.",
      },
    ],
  },

  // ==========================================================
  // WHATSAPP API SERVICES
  // ==========================================================

  whatsappApi: {
    id: "whatsappApi",

    name: "WhatsApp API Services",

    shortDescription:
      "Business messaging, automation, notifications, and verification",

    description:
      "We provide business communication solutions that help organizations automate messaging, notifications, customer communication, and secure verification through reliable messaging infrastructure.",

    subServices: [
      {
        id: "businessSms",

        name: "Business SMS Services",

        description:
          "Our Business SMS solutions help businesses send transactional, promotional, bulk, and customer communication messages efficiently and reliably.",
      },

      {
        id: "secureOtp",

        name: "Secure OTP Services",

        description:
          "Our Secure OTP solutions help businesses implement reliable verification and authentication through secure one-time passwords for customer and user verification.",
      },
    ],
  },

  // ==========================================================
  // LEGAL SERVICES
  // ==========================================================

  legalServices: {
    id: "legalServices",

    name: "Legal Services",

    shortDescription:
      "Business registration, compliance, taxation, and certifications",

    description:
      "We provide professional business and legal support services to help organizations manage registrations, compliance, taxation, certifications, and intellectual property requirements.",

    subServices: [
      {
        id: "businessRegistration",

        name: "Business Registration",

        description:
          "We assist businesses with registration requirements and help them establish the appropriate legal structure for their operations.",
      },

      {
        id: "companyRegistration",

        name: "Company Registration",

        description:
          "We provide support for company incorporation and registration requirements, helping businesses complete the necessary formalities efficiently.",
      },

      {
        id: "taxation",

        name: "Taxation Services",

        description:
          "We provide taxation-related assistance to help businesses manage their tax requirements, compliance, documentation, and related processes.",
      },

      {
        id: "isoCertification",

        name: "ISO Certification",

        description:
          "We assist businesses with ISO certification processes and help organizations work toward meeting applicable quality and management standards.",
      },

      {
        id: "ipr",

        name: "IPR Services",

        description:
          "We provide intellectual property related support covering areas such as trademarks, copyrights, patents, and other intellectual property requirements.",
      },
    ],
  },

  // ==========================================================
  // PRODUCT DEVELOPMENT
  // ==========================================================

  productDevelopment: {
    id: "productDevelopment",

    name: "Product Development",

    shortDescription:
      "New product development services — coming soon",

    comingSoon: true,

    description:
      "Our Product Development services are currently under development and will be available soon.",

    subServices: [],
  },
};

// ============================================================
// AVAILABLE MAIN SERVICES
// ============================================================

export const availableServices = [
  servicesData.softwareDevelopment,
  servicesData.whatsappApi,
  servicesData.legalServices,
  servicesData.productDevelopment,
];

// ============================================================
// NORMALIZE TEXT
// ============================================================

const normalizeText = (value = "") => {
  return value
    .toLowerCase()
    .replace(/[^\w\s&/+.-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// ============================================================
// SERVICE KEYWORDS
// ============================================================

const serviceKeywords = {
  // ----------------------------------------------------------
  // SOFTWARE DEVELOPMENT
  // ----------------------------------------------------------

  softwareDevelopment: [
    "software",
    "software development",
    "custom software",
    "custom software development",

    "mobile",
    "mobile app",
    "mobile application",
    "mobile app development",

    "web",
    "website",
    "web development",
    "web application",
    "web app",

    "ui ux",
    "ui/ux",
    "ui ux development",
    "user interface",
    "user experience",

    "crm",
    "crm development",
    "customer relationship",

    "mlm",
    "mlm development",
    "network marketing software",
  ],

  // ----------------------------------------------------------
  // WHATSAPP API SERVICES
  // ----------------------------------------------------------

  whatsappApi: [
    "whatsapp",
    "whatsapp api",
    "whatsapp api services",

    "business sms",
    "business sms services",

    "sms",
    "sms service",
    "sms services",

    "secure otp",
    "otp",
    "otp service",
    "otp services",

    "verification",
    "authentication",

    "business messaging",
    "messaging",
  ],

  // ----------------------------------------------------------
  // LEGAL SERVICES
  // ----------------------------------------------------------

  legalServices: [
    "legal",
    "legal services",

    "business registration",
    "company registration",
    "company incorporation",
    "incorporation",

    "tax",
    "taxation",
    "taxation services",

    "iso",
    "iso certification",
    "certification",

    "ipr",
    "intellectual property",
    "trademark",
    "copyright",
    "patent",
  ],

  // ----------------------------------------------------------
  // PRODUCT DEVELOPMENT
  // ----------------------------------------------------------

  productDevelopment: [
    "product development",
    "product",
    "new product",
    "product development service",
  ],
};

// ============================================================
// FIND MAIN SERVICE FROM MESSAGE
// ============================================================

export function findServiceFromMessage(message = "") {
  const normalizedMessage = normalizeText(message);

  if (!normalizedMessage) {
    return null;
  }

  // More specific services are checked first.
  const serviceOrder = [
    "productDevelopment",
    "softwareDevelopment",
    "whatsappApi",
    "legalServices",
  ];

  for (const serviceId of serviceOrder) {
    const service = servicesData[serviceId];

    const keywords = serviceKeywords[serviceId] || [];

    const matched = keywords.some((keyword) => {
      const normalizedKeyword = normalizeText(keyword);

      return normalizedMessage.includes(
        normalizedKeyword,
      );
    });

    if (matched) {
      return service;
    }
  }

  return null;
}

// ============================================================
// SUBSERVICE KEYWORDS
// ============================================================

const subServiceKeywords = {
  // ==========================================================
  // SOFTWARE DEVELOPMENT
  // ==========================================================

  customSoftware: [
    "custom software",
    "custom software development",
    "custom application",
    "custom app",
  ],

  mobileApp: [
    "mobile app",
    "mobile application",
    "mobile app development",
    "android app",
    "ios app",
    "ios",
    "android",
  ],

  webDevelopment: [
    "web development",
    "website development",
    "web application",
    "web app",
    "website",
    "web development service",
  ],

  uiux: [
    "ui ux",
    "ui/ux",
    "ui ux development",
    "user interface",
    "user experience",
    "ui design",
    "ux design",
  ],

  crm: [
    "crm",
    "crm development",
    "customer relationship management",
  ],

  mlm: [
    "mlm",
    "mlm development",
    "network marketing",
    "network marketing software",
  ],

  // ==========================================================
  // WHATSAPP API SERVICES
  // ==========================================================

  businessSms: [
    "business sms",
    "business sms services",
    "bulk sms",
    "transactional sms",
    "promotional sms",
    "sms api",
  ],

  secureOtp: [
    "secure otp",
    "otp",
    "otp service",
    "otp services",
    "one time password",
    "one-time password",
    "verification",
    "authentication",
  ],

  // ==========================================================
  // LEGAL SERVICES
  // ==========================================================

  businessRegistration: [
    "business registration",
    "business registration service",
  ],

  companyRegistration: [
    "company registration",
    "company incorporation",
    "incorporation",
  ],

  taxation: [
    "taxation",
    "tax service",
    "tax services",
    "tax",
  ],

  isoCertification: [
    "iso certification",
    "iso certification service",
    "iso",
  ],

  ipr: [
    "ipr",
    "intellectual property",
    "trademark",
    "copyright",
    "patent",
  ],
};

// ============================================================
// FIND SUBSERVICE
// ============================================================

export function findSubServiceFromMessage(message = "") {
  const normalizedMessage = normalizeText(message);

  if (!normalizedMessage) {
    return null;
  }

  // ----------------------------------------------------------
  // Check all available services
  // ----------------------------------------------------------

  for (const service of Object.values(servicesData)) {
    if (!service.subServices?.length) {
      continue;
    }

    for (const subService of service.subServices) {
      const keywords =
        subServiceKeywords[subService.id] || [];

      const matched = keywords.some((keyword) => {
        const normalizedKeyword =
          normalizeText(keyword);

        return normalizedMessage.includes(
          normalizedKeyword,
        );
      });

      if (matched) {
        return {
          service,
          subService,
        };
      }
    }
  }

  return null;
}

// ============================================================
// GENERAL SERVICE KEYWORDS
// ============================================================

export const generalServiceKeywords = [
  "service",
  "services",

  "provide",
  "provides",
  "provided",

  "offer",
  "offers",
  "offering",

  "available",

  "what do you",
  "what can you",
  "what type",

  "tell me about",

  "interested",

  "solution",
  "solutions",

  "what services",
  "your services",
];